import { CalendarCheck, ChevronsLeftRight, CornerDownRight } from 'lucide-react';
import { Icon } from '../../design-system/icons';
import { dsColors } from '../../design-system/tokens/colors';
import { ULTRA_TIER_LABELS, type PricingPlanContent } from './pricingContent';

/**
 * Card blanca con el detalle del plan seleccionado — frame "Plan Details" del
 * rediseño de Figma (node 2046:346). Ver ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales.
 *
 * Es puramente presentacional: recibe los precios ya formateados por
 * `Paywall` (que es quien conoce país, moneda y cupón) y no calcula nada.
 */

export interface PlanCardPriceProps {
  /** Precio vigente, ya formateado (ej. `$69.999 ARS`). */
  price: string;
  /** Leyenda del período: `por mes`, o `el primer mes` cuando hay descuento. */
  period: string;
  /** Precio de lista tachado. Sólo con descuento. */
  strikePrice?: string;
  /** Badge de descuento (ej. `30% OFF`). Sólo con descuento. */
  discountLabel?: string;
  /**
   * Aclaración en su propia línea, debajo del precio (ej. el total anual). Va
   * aparte de `period` porque en la misma línea que el precio grande el renglón
   * quedaba largo y le competía.
   */
  priceNote?: string;
  /** Leyenda "IVA incluido" bajo el precio. Sólo cuentas argentinas. */
  ivaIncluido?: boolean;
}

export interface PlanCardProps extends PlanCardPriceProps {
  content: PricingPlanContent;
  /** Créditos mensuales a mostrar (Ultra los pisa según el tier elegido). */
  credits: number;
  /** Líneas de "Se pueden generar:" (Ultra las pisa según el tier). */
  generates: string[];
  /** Slider de tiers — sólo Ultra. */
  ultraTier?: {
    index: number;
    count: number;
    onChange: (index: number) => void;
  };
  /** Marca la card como el plan actual del usuario. */
  isCurrent?: boolean;
  /** CTA embebido en la card (desktop). En mobile el CTA va fijo al pie. */
  footer?: React.ReactNode;
  /**
   * Aire reducido. En el modal de desktop la card comparte alto con dos
   * segmentados y el pie, y el plan más largo (Ultra) no entraba entero: unos
   * pocos px por bloque son lo que separa "se lee todo" de "hay que scrollear".
   */
  compact?: boolean;
  className?: string;
}

export function PlanCard({
  content,
  credits,
  generates,
  price,
  period,
  strikePrice,
  discountLabel,
  priceNote,
  ivaIncluido,
  ultraTier,
  isCurrent,
  footer,
  compact = false,
  className = '',
}: PlanCardProps) {
  return (
    <div
      // `gap-3.5` en compact (ADR-2026-07-31-menu-cuenta-avatar-uno-solo): son 2px menos por junta, imperceptibles a
      // esta escala, y es lo que hace entrar el caso más alto —Ultra con cupón, que
      // suma precio tachado y el slider de tiers— sin recortar la card.
      className={`flex flex-col rounded-2xl border px-6 ${
        compact ? 'gap-3.5 py-4' : 'gap-6 py-5'
      } ${className}`}
      // El borde es SIEMPRE neutro, también en el plan vigente. Antes se pintaba
      // de azul de marca cuando `isCurrent`, y en una pantalla donde la selección
      // es el segmentado de arriba, un recuadro azul alrededor de la card se leía
      // como "esta card está seleccionada" — que es otra cosa. Que es tu plan lo
      // dice el badge "Tu plan", que no se presta a esa confusión (ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil).
      style={{
        background: dsColors.neutral[50],
        borderColor: dsColors.neutral[100],
      }}
    >
      {/* Título + bajada */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h3
            className="text-[24px] font-[700] leading-[31px]"
            style={{ color: dsColors.neutral[900] }}
          >
            {content.title}
          </h3>
          {isCurrent ? (
            <span
              className="rounded-lg px-2 py-0.5 text-[10px] font-[700] leading-[17px] text-white"
              style={{ background: dsColors.brand[500] }}
            >
              Tu plan
            </span>
          ) : null}
        </div>
        {/* 13px y no 14: los textos DESCRIPTIVOS de la card bajaron un escalón
            (ADR-2026-07-31-menu-cuenta-avatar-uno-solo) para que respiren mejor sin estirar el alto. El bloque de
            precio y la cifra de créditos NO bajan — son las cifras que tienen que
            pesar más que su descripción. */}
        <p
          className="text-[13px] font-[400] leading-[18px]"
          style={{ color: dsColors.neutral[600] }}
        >
          {content.subtitle}
        </p>
      </div>

      {/* Precio — con o sin descuento */}
      <div className="flex flex-col gap-1">
        {strikePrice ? (
          <div className="flex flex-wrap items-center gap-4">
            <span
              className="text-[20px] font-[600] leading-[25px] line-through"
              style={{ color: dsColors.neutral[500] }}
            >
              {strikePrice}
            </span>
            {discountLabel ? (
              <span
                className="rounded-lg px-1.5 py-0.5 text-[10px] font-[700] leading-[17px] text-white"
                style={{ background: dsColors.brand[500] }}
              >
                {discountLabel}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="flex flex-wrap items-baseline gap-2">
          <span
            className="text-[24px] font-[700] leading-[31px]"
            style={{ color: dsColors.neutral[900] }}
          >
            {price}
          </span>
          <span
            className="text-[14px] font-[600] leading-[19px]"
            style={{ color: dsColors.neutral[600] }}
          >
            {period}
          </span>
        </div>
        {priceNote ? (
          <p
            className="text-[14px] font-[600] leading-[19px]"
            style={{ color: dsColors.neutral[600] }}
          >
            {priceNote}
          </p>
        ) : null}
        {ivaIncluido ? (
          <p
            className="text-[12px] font-[400] leading-[16px]"
            style={{ color: dsColors.neutral[500] }}
          >
            IVA incluido
          </p>
        ) : null}
      </div>

      {/* Caja de créditos (+ slider de tiers en Ultra) */}
      <div
        className={`flex flex-col rounded-xl border px-4 py-3 ${
          compact ? 'gap-2.5' : 'gap-3'
        }`}
        style={{
          background: dsColors.base.white,
          borderColor: dsColors.neutral[100],
        }}
      >
        <div className="flex items-center gap-1">
          <Icon
            name="solid/astroid"
            size={14}
            className="shrink-0"
            style={{ color: dsColors.brand[500] }}
          />
          <span
            className="text-[16px] font-[600] leading-[21px]"
            style={{ color: dsColors.brand[500] }}
          >
            {credits.toLocaleString('es-AR')} créditos
          </span>
          <span
            className="text-[14px] font-[600] leading-[19px]"
            style={{ color: dsColors.neutral[600] }}
          >
            por mes
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <p
            className="text-[13px] font-[400] leading-[18px]"
            style={{ color: dsColors.neutral[600] }}
          >
            Se pueden generar:
          </p>
          {/* Las líneas son ALTERNATIVAS, no una suma: los créditos son una bolsa
              única y un segundo de video cuesta 2 contra 1 de una imagen. Sin el
              "o", "250 imágenes / 125 segundos" se lee como que se
              obtienen las dos cosas, que es el doble de lo que alcanza el plan.
              Ver ADR-2026-08-03-creditos-por-plan-mienten. */}
          {generates.map((line, i) => (
            <p
              key={line}
              className="text-[13px] font-[600] leading-[18px]"
              style={{ color: dsColors.neutral[900] }}
            >
              {i > 0 ? `o ${line}` : line}
            </p>
          ))}
        </div>
        {ultraTier ? (
          <UltraTierSlider
            index={ultraTier.index}
            count={ultraTier.count}
            onChange={ultraTier.onChange}
          />
        ) : null}
      </div>

      {/* Beneficio destacado (Ultra): un servicio, no una feature de la app, así
          que va en su propia caja y no perdido entre los checks. */}
      {content.highlight ? (
        <div
          className="flex items-start gap-2 rounded-xl border px-4 py-3"
          style={{
            background: dsColors.base.white,
            borderColor: dsColors.neutral[100],
          }}
        >
          <CalendarCheck
            size={16}
            className="mt-[1px] shrink-0"
            style={{ color: dsColors.brand[500] }}
          />
          <div className="flex flex-col gap-0.5">
            <span
              className="text-[14px] font-[600] leading-[19px]"
              style={{ color: dsColors.neutral[900] }}
            >
              {content.highlight.title}
            </span>
            <span
              className="text-[13px] font-[400] leading-[18px]"
              style={{ color: dsColors.neutral[600] }}
            >
              {content.highlight.description}
            </span>
          </div>
        </div>
      ) : null}

      {/* Features */}
      <div className="flex flex-col gap-2">
        {content.inherits ? (
          <div className="flex items-start gap-1.5">
            <CornerDownRight
              size={18}
              className="shrink-0"
              style={{ color: dsColors.neutral[500] }}
            />
            <span
              className="text-[13px] font-[600] leading-[18px]"
              style={{ color: dsColors.neutral[500] }}
            >
              {content.inherits}
            </span>
          </div>
        ) : null}
        {content.features.map((feature) => (
          <div key={feature} className="flex items-start gap-1.5">
            <Icon
              name="outline/check"
              size={18}
              className="shrink-0"
              style={{ color: dsColors.neutral[900] }}
            />
            <span
              className="text-[13px] font-[600] leading-[18px]"
              style={{ color: dsColors.neutral[900] }}
            >
              {feature}
            </span>
          </div>
        ))}
        {content.excluded?.map((item) => (
          <div key={item} className="flex items-start gap-1.5">
            <Icon
              name="outline/cross"
              size={18}
              className="shrink-0"
              style={{ color: dsColors.neutral[400] }}
            />
            <span
              className="text-[13px] font-[400] leading-[18px]"
              style={{ color: dsColors.neutral[500] }}
            >
              {item}
            </span>
          </div>
        ))}
      </div>

      {/* `mt-auto` empuja el CTA al pie: con las cards estiradas a la misma
          altura (desktop), los 3 botones quedan alineados. */}
      {footer ? <div className="mt-auto pt-2">{footer}</div> : null}
    </div>
  );
}

/**
 * Slider de tiers de Ultra (frame "Pricing Slider Container", node 2046:792).
 * Es un `range` nativo estilado: así se arrastra, responde al teclado y anuncia
 * su valor sin reimplementar el gesto a mano.
 */
export function UltraTierSlider({
  index,
  count,
  onChange,
}: {
  index: number;
  count: number;
  onChange: (index: number) => void;
}) {
  const max = count - 1;
  const pct = max > 0 ? (index / max) * 100 : 0;

  return (
    <div className="flex flex-col gap-3 pt-1">
      <div className="relative h-4">
        {/* Riel + relleno */}
        <div
          className="absolute inset-x-0 top-1/2 h-[6px] -translate-y-1/2 rounded-full"
          style={{ background: dsColors.neutral[100] }}
        />
        <div
          className="absolute top-1/2 h-[6px] -translate-y-1/2 rounded-full transition-[width] duration-200 ease-out"
          style={{ width: `${pct}%`, background: dsColors.brand[500] }}
        />
        {/* Manija */}
        <div
          className="pointer-events-none absolute top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full transition-[left] duration-200 ease-out"
          style={{
            left: `calc(${pct}% - ${(pct / 100) * 28}px)`,
            background: dsColors.brand[500],
          }}
        >
          {/* `<>` (chevrons hacia afuera), como el handle del frame. El DS sólo
              tiene los dobles chevrons a un lado, así que acá va el de lucide. */}
          <ChevronsLeftRight size={16} style={{ color: dsColors.neutral[50] }} />
        </div>
        <input
          type="range"
          min={0}
          max={max}
          step={1}
          value={index}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label="Cantidad de imágenes por mes"
          aria-valuetext={`${ULTRA_TIER_LABELS[index] ?? index} imágenes por mes`}
          className="absolute inset-0 size-full cursor-pointer opacity-0"
          // `touch-action: none` — sin esto, en mobile este slider vive DENTRO del
          // carrusel horizontal de planes (ADR-2026-08-03-cards-de-planes-deslizables-en-mobile) y arrastrarlo
          // paneaba el carrusel a la card de al lado en vez de mover el tier. Le
          // decimos al navegador que no interprete el gesto: el `input range` lo
          // maneja él. El costo es que un scroll vertical iniciado justo sobre estos
          // 16px no arrastra la página — es el comportamiento habitual de un slider.
          style={{ touchAction: 'none' }}
        />
      </div>
      {/* Topes */}
      <div className="flex items-center justify-between">
        {ULTRA_TIER_LABELS.slice(0, count).map((label, i) => (
          <button
            key={label}
            type="button"
            onClick={() => onChange(i)}
            className="flex items-center gap-1 transition-colors duration-200"
          >
            <Icon
              name="solid/astroid"
              size={14}
              className="shrink-0"
              style={{
                color: i === index ? dsColors.brand[500] : dsColors.neutral[500],
              }}
            />
            <span
              className="text-[14px] font-[600] leading-[19px]"
              style={{
                color: i === index ? dsColors.neutral[900] : dsColors.neutral[500],
              }}
            >
              {label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default PlanCard;
